import { ExEmptyState, ExIcon } from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import Loading from '../Loading';
import { type AutomateError, getAuthenticationStatus } from '../api';
import AnimatedLink from '../common/AnimatedLink';
import { useApp } from '../common/AppProvider';
import ConfirmAction from '../common/ConfirmAction';
import FlipCard from '../common/FlipCard';
import { translations } from '../translations/translations';
import {
    type AuthenticationStatusResponse,
    type AutomationTask,
    AutomationTaskStatus,
} from '../types/types';
import { getConnectorIdsFromTasks, isTaskAuthenticated } from '../utils/utils';
import StatInfo from './StatInfo';
import TaskItem from './TaskItem';
import TasksFilters from './TasksFilters';
// Sidebar will remain hidden during beta. We'll re-enable it when plans and pricing are ready.
// import PlanInfo from './PlanInfo';
// import UsageInfo from './UsageInfo';

import './task-list.css';

const Tasks = () => {
    const {
        tasks,
        filteredTasks,
        syncAndUpdate,
        language,
        isLoading,
        alertError,
        getTaskActivations,
        getTaskMinutesSaved,
    } = useApp();
    const [deletingTask, setDeletingTask] = useState<string>('');
    const [authenticationStatus, setAuthenticationStatus] = useState<AuthenticationStatusResponse>(
        {},
    );

    const activations = tasks.reduce(
        (accumulator: number, currentValue: AutomationTask) =>
            accumulator + getTaskActivations(currentValue),
        0,
    );

    const minutes = tasks.reduce(
        (accumulator: number, currentValue: AutomationTask) =>
            accumulator + getTaskMinutesSaved(currentValue),
        0,
    );

    const activeTasks = tasks.reduce(
        (accumulator: number, currentValue: AutomationTask) =>
            accumulator + (currentValue.isActive ? 1 : 0),
        0,
    );

    const handleDeletingTask = (taskId: string) => {
        const task = filteredTasks.find((t) => t.id === taskId);

        if (task) {
            task.status = AutomationTaskStatus.Deleted;
            syncAndUpdate([task]);
        }

        setDeletingTask('');
    };

    useEffect(() => {
        const load = async () => {
            const connectorIds = getConnectorIdsFromTasks(tasks);

            if (connectorIds.length > 0) {
                try {
                    setAuthenticationStatus(await getAuthenticationStatus(connectorIds));
                } catch (error) {
                    alertError({
                        message: translations[language].ERROR_getting_authentication_status,
                        traceId: (error as AutomateError).json?.traceId,
                    });
                }
            }
        };
        load();
    }, [tasks, alertError, language]);

    return (
        <div className="task-list-page">
            <section className="task-list-main">
                {/* TASK STATS */}
                <section className="task-list-dashboard">
                    <StatInfo
                        title={translations[language].TASKS_total_tasks_label}
                        value={tasks.length}
                        unit={translations[language].TASKS_unit_tasks}
                    />
                    <StatInfo
                        title={translations[language].TASKS_active_tasks_label}
                        value={activeTasks}
                        unit={translations[language].TASKS_unit_tasks}
                    />
                    <StatInfo
                        title={translations[language].TASKS_activations_label}
                        value={activations}
                        unit={translations[language].TASKS_unit_activations}
                    />
                    <StatInfo
                        title={translations[language].TASKS_time_saved_label}
                        value={minutes}
                        unit={translations[language].TASKS_unit_minutes}
                    />
                </section>

                {/* TASK LIST */}
                <section className="task-list-content">
                    <h2 className="title xxl">{translations[language].TASKS_my_tasks}</h2>

                    <TasksFilters />

                    {isLoading && <Loading message={translations[language].COMMON_loading} />}

                    {!!filteredTasks.length && (
                        <ul className="task-list">
                            {filteredTasks.map((task) => {
                                return (
                                    <li
                                        key={task.id}
                                        className={`task-list-item ${
                                            task.isActive ? '' : 'task-list-item-off'
                                        }`}
                                    >
                                        <FlipCard
                                            flipOver={deletingTask === task.id}
                                            front={
                                                <TaskItem
                                                    task={task}
                                                    setDeletingTask={setDeletingTask}
                                                    isAuthenticated={isTaskAuthenticated(
                                                        task,
                                                        authenticationStatus,
                                                    )}
                                                />
                                            }
                                            back={
                                                <ConfirmAction
                                                    messages={[
                                                        translations[language]
                                                            .TASKS_delete_confirm_message,
                                                    ]}
                                                    onConfirm={() => handleDeletingTask(task.id)}
                                                    onCancel={() => setDeletingTask('')}
                                                    testId={`confirmActionPanel${task.id}`}
                                                />
                                            }
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {/*
                        Show this when no tasks exist.
                    */}
                    {!(isLoading || tasks.length) && (
                        <ExEmptyState
                            className="empty-state"
                            label={translations[language].COMMON_no_results}
                            text={translations[language].TASKS_no_tasks}
                        >
                            <ExIcon className="empty-state-icon" slot="header" icon="community" />
                            <span slot="action">
                                <AnimatedLink className="btn-text" to="/tasks/new">
                                    {translations[language].TASK_BUILDER_create}
                                </AnimatedLink>
                            </span>
                        </ExEmptyState>
                    )}

                    {/*
                        Show this when tasks exist but the user filters/search produce no results.
                    */}
                    {!isLoading && !!tasks.length && !filteredTasks.length && (
                        <ExEmptyState
                            className="empty-state"
                            label={translations[language].COMMON_no_results}
                            text={translations[language].COMMON_search_hint}
                        >
                            <ExIcon className="empty-state-icon" slot="header" icon="community" />
                        </ExEmptyState>
                    )}
                </section>
            </section>

            {/* SIDEBAR CONTENT */}
            {/* Sidebar will remain hidden during beta. We'll re-enable it when plans and pricing are ready. */}
            {/*
            <section className="task-list-sidebar">
                <PlanInfo />
                <UsageInfo />
            </section>
            */}
        </div>
    );
};

export default Tasks;
